import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import MagneticButton from "./MagneticButton";
import { Link } from "react-router-dom";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  return (
    <section ref={ref} className="relative h-screen w-full overflow-hidden bg-espresso text-ivory" data-testid="hero-section">
      {/* Video layer */}
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="w-full h-full object-cover bg-espresso"
          aria-hidden="true"
        >
          <source src="/media/videos/salumi-1.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-espresso/55" />
        <div className="absolute inset-0 light-leak opacity-60" />
      </motion.div>

      {/* Floating decorative olive accents */}
      <motion.div
        className="absolute top-[18%] right-[7%] hidden md:block text-ivory/30"
        animate={{ y: [0, -10, 0], rotate: [0, 6, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden="true"
      >
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M30 5 C 38 15, 38 25, 30 35 C 22 25, 22 15, 30 5 Z" stroke="currentColor" strokeWidth="0.7" fill="none" />
          <path d="M30 35 L 30 55" stroke="currentColor" strokeWidth="0.6" />
        </svg>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative h-full max-w-[1480px] mx-auto px-6 md:px-10 flex flex-col justify-end pt-32 md:pt-56 pb-24 md:pb-32"
      >
        <motion.h1
          className="font-serif text-ivory tracking-editorial leading-[0.92] max-w-5xl"
          style={{ fontSize: "clamp(2.1rem, 7vw, 6.5rem)" }}
        >
          {["Hands-on", "culinary", "experiences"].map((word, i) => (
            <motion.span
              key={word}
              initial={{ opacity: 0, y: 60, filter: "blur(12px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 1.2, delay: 0.55 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="inline-block mr-[0.25em]"
            >
              {word}
            </motion.span>
          ))}
          <br />
          <motion.span
            initial={{ opacity: 0, y: 60, filter: "blur(12px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.2, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
            className="italic text-terracotta font-light"
          >
            in{" "}
            <span className="relative inline-block">
              Tucson
              <motion.svg
                aria-hidden="true"
                viewBox="0 0 200 18"
                preserveAspectRatio="none"
                className="absolute left-0 -bottom-[0.18em] w-full pointer-events-none"
                style={{ height: "0.22em", overflow: "visible" }}
              >
                <motion.path
                  d="M 2 12 Q 18 2, 36 10 T 72 11 T 108 9 T 144 12 T 180 8 T 198 11"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{
                    pathLength: { duration: 1.4, delay: 1.7, ease: [0.65, 0, 0.35, 1] },
                    opacity: { duration: 0.3, delay: 1.7 },
                  }}
                />
              </motion.svg>
            </span>
            .
          </motion.span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20, filter: "blur(6px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 max-w-xl text-base md:text-lg text-ivory/75 leading-relaxed"
        >
          Private cooking classes, seasonal workshops, fresh pasta, craft cocktails, and unforgettable gatherings — curated around food, creativity, and community.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.45 }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <Link to="/private-bookings#contact" data-testid="hero-book-private">
            <MagneticButton variant="ivory" as="span">
              Book a Private Experience
            </MagneticButton>
          </Link>
          <Link to="/classes" data-testid="hero-explore-classes">
            <MagneticButton variant="outline" as="span" className="!border-ivory/50 !text-ivory hover:!bg-ivory hover:!text-espresso">
              Explore Upcoming Classes
            </MagneticButton>
          </Link>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-ivory/60 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ArrowDown strokeWidth={1.25} className="size-4" />
        </motion.div>
      </motion.div>
    </section>
  );
}
